/**
 * Parent → Child impersonation helper.
 *
 * Lets a logged-in parent temporarily browse the platform AS one of their
 * linked children, then switch back to their parent session cleanly. The
 * "original" parent session is squirreled away under a dedicated localStorage
 * key so we can restore it even after a page refresh.
 */

import axios from 'axios';

const ORIGINAL_KEY = 'parentImpersonationOriginal';

/** Read the current original-parent payload (if any). */
export const getOriginalParent = () => {
  try {
    const raw = localStorage.getItem(ORIGINAL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** Is the current session a parent-as-child impersonation? */
export const isImpersonating = () => !!getOriginalParent();

/**
 * Begin impersonating the given child. Calls the backend to mint a child
 * token, then swaps the current localStorage user/token with the child's.
 *
 * @param {string} apiBase - REACT_APP_BACKEND_URL value (no trailing slash)
 * @param {string} childId - The linked child's user id
 * @returns {Promise<object>} - The child user object
 */
export const startImpersonation = async (apiBase, childId) => {
  const parentToken = localStorage.getItem('token');
  const parentUserRaw = localStorage.getItem('user');
  if (!parentToken || !parentUserRaw) {
    throw new Error('Parent session missing — please log in again.');
  }

  // Stash the parent session BEFORE swapping (so a page refresh mid-flow is recoverable).
  const original = {
    token: parentToken,
    user: JSON.parse(parentUserRaw),
    startedAt: new Date().toISOString(),
  };
  localStorage.setItem(ORIGINAL_KEY, JSON.stringify(original));

  const resp = await axios.post(
    `${apiBase}/api/parent/impersonate/${childId}`,
    {},
    { headers: { Authorization: `Bearer ${parentToken}` } }
  );

  const { token: childToken, user: childUser } = resp.data || {};
  if (!childToken || !childUser) {
    // Roll back the stash if the backend failed mid-flight.
    localStorage.removeItem(ORIGINAL_KEY);
    throw new Error('Could not start impersonation.');
  }

  localStorage.setItem('token', childToken);
  localStorage.setItem('user', JSON.stringify(childUser));
  // Reset transient student-flow flags so the child view starts in a known state.
  localStorage.removeItem('studentProgress');

  return childUser;
};

/**
 * End impersonation: restore the parent's token + user, clear the flag,
 * then a full reload to re-bootstrap the App identity cleanly.
 */
export const endImpersonation = () => {
  const original = getOriginalParent();
  if (!original) {
    return false;
  }
  localStorage.setItem('token', original.token);
  localStorage.setItem('user', JSON.stringify(original.user));
  localStorage.removeItem(ORIGINAL_KEY);
  localStorage.removeItem('studentProgress');
  return true;
};
